import React, { useMemo } from "react";

const ArcGauge = ({ pct = 0, color }) => {
  const R = 22;
  const cx = 30;
  const cy = 30;

  const toRad = (deg) => (deg * Math.PI) / 180;

  const arcPath = (from, to) => {
    const x1 = cx + R * Math.cos(toRad(from));
    const y1 = cy + R * Math.sin(toRad(from));
    const x2 = cx + R * Math.cos(toRad(to));
    const y2 = cy + R * Math.sin(toRad(to));
    const largeArc = to - from > 180 ? 1 : 0;
    return `M ${x1} ${y1} A ${R} ${R} 0 ${largeArc} 1 ${x2} ${y2}`;
  };

  const fillAngle = -180 + (Math.min(pct, 100) / 100) * 180;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 2,
      }}
    >
      <svg width={60} height={34} viewBox="0 0 60 34">
        <path
          d={arcPath(-180, 0)}
          fill="none"
          stroke="#e8e8e8"
          strokeWidth={5}
          strokeLinecap="round"
        />
        {pct > 0 && (
          <path
            d={arcPath(-180, fillAngle)}
            fill="none"
            stroke={color}
            strokeWidth={5}
            strokeLinecap="round"
          />
        )}
      </svg>
      <span
        style={{
          fontSize: 13,
          fontWeight: 700,
          color: color,
          background: color + "18",
          borderRadius: 6,
          padding: "1px 8px",
          marginTop: -6,
        }}
      >
        {pct}%
      </span>
    </div>
  );
};

const QUIZ_COLOR = "#9b59b6";

const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

// data shape: [{ category: "Sales", value: 80 }, ...]
// quizData shape: [{ quizId, quizName, quizDate, score }]
const ScoreBoardTable = ({ data = [], totalScore = 0, quizData = [] }) => {
  const avgQuizScore = useMemo(() => {
    if (quizData.length === 0) return Math.round(totalScore);
    const sum = quizData.reduce((acc, q) => acc + (q.score ?? 0), 0);
    return Math.round(sum / quizData.length);
  }, [quizData, totalScore]);

  const subHeaderBase = {
    fontSize: 11,
    fontWeight: 600,
    color: "#999",
    letterSpacing: "0.08em",
    textAlign: "center",
    padding: "8px 16px",
    borderBottom: "1px solid #eee",
  };

  return (
    <div
      style={{
        width: "100%",
        fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
        background: "#f7f8fc",
        borderRadius: 16,
        padding: "28px 24px",
      }}
    >
      {/* Title row */}
      <div
        style={{
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "space-between",
          marginBottom: 20,
        }}
      >
        <div>
          <div
            style={{
              fontSize: 11,
              fontWeight: 600,
              color: "#aaa",
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              marginBottom: 4,
            }}
          >
            Staff Performance
          </div>
          <div style={{ fontSize: 24, fontWeight: 700, color: "#1a1a2e" }}>
            Score Board
          </div>
        </div>

        {/* Total score badge */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            background: "#fff",
            borderRadius: 14,
            padding: "12px 28px",
            boxShadow: "0 2px 12px rgba(0,0,0,0.07)",
            border: "1px solid #f0f0f0",
          }}
        >
          <span
            style={{
              fontSize: 11,
              fontWeight: 600,
              color: "#aaa",
              letterSpacing: "0.08em",
              textTransform: "uppercase",
            }}
          >
            Total Average Score
          </span>
          <span
            style={{
              fontSize: 28,
              fontWeight: 800,
              color:
                avgQuizScore >= 80
                  ? "#27ae60"
                  : avgQuizScore >= 60
                    ? "#f5a623"
                    : "#e05c5c",
              lineHeight: 1.2,
            }}
          >
            {avgQuizScore}
            <span style={{ fontSize: 14, fontWeight: 500, color: "#aaa" }}>
              /100
            </span>
          </span>
        </div>
      </div>

      {/* Quiz list table */}
      {quizData.length > 0 && (
        <div
          style={{
            background: "#fff",
            borderRadius: 14,
            overflow: "hidden",
            boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
          }}
        >
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th
                  style={{
                    width: "40%",
                    padding: "16px 20px",
                    textAlign: "left",
                    fontSize: 11,
                    fontWeight: 600,
                    color: "#bbb",
                    letterSpacing: "0.1em",
                    background: "#fafafa",
                    borderBottom: "1px solid #f0f0f0",
                  }}
                >
                  QUIZ NAME
                </th>
                <th
                  style={{
                    ...subHeaderBase,
                    background: "#fafafa",
                    borderBottom: "1px solid #f0f0f0",
                  }}
                >
                  DATE
                </th>
                <th
                  style={{
                    ...subHeaderBase,
                    background: "#fafafa",
                    borderBottom: "1px solid #f0f0f0",
                  }}
                >
                  SCORE
                </th>
                <th
                  style={{
                    ...subHeaderBase,
                    background: "#fafafa",
                    borderBottom: "1px solid #f0f0f0",
                  }}
                >
                  ACHIEVEMENT
                </th>
              </tr>
            </thead>
            <tbody>
              {quizData.map((quiz, i) => {
                const score = Math.round(quiz.score ?? 0);
                const color =
                  score >= 80 ? "#27ae60" : score >= 60 ? "#f5a623" : "#e05c5c";

                return (
                  <tr
                    key={quiz.quizId || i}
                    style={{
                      borderBottom:
                        i < quizData.length - 1 ? "1px solid #f3f3f3" : "none",
                      transition: "background 0.15s",
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.background = "#fafbff")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.background = "transparent")
                    }
                  >
                    {/* Quiz Name with colored dot */}
                    <td style={{ padding: "16px 20px" }}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 10,
                        }}
                      >
                        <span
                          style={{
                            width: 10,
                            height: 10,
                            borderRadius: "50%",
                            background: QUIZ_COLOR,
                            display: "inline-block",
                            flexShrink: 0,
                          }}
                        />
                        <span
                          style={{
                            fontWeight: 700,
                            fontSize: 15,
                            color: "#222",
                          }}
                        >
                          {quiz.quizName}
                        </span>
                      </div>
                    </td>

                    {/* Date */}
                    <td
                      style={{
                        textAlign: "center",
                        padding: "16px 10px",
                        fontSize: 14,
                        color: "#888",
                        fontWeight: 500,
                      }}
                    >
                      {formatDate(quiz.quizDate)}
                    </td>

                    {/* Raw score */}
                    <td
                      style={{
                        textAlign: "center",
                        padding: "16px 10px",
                        fontSize: 15,
                        fontWeight: 600,
                        color: color,
                        borderLeft: `3px solid ${color}`,
                      }}
                    >
                      {score}
                    </td>

                    {/* Arc gauge */}
                    <td style={{ textAlign: "center", padding: "10px" }}>
                      <ArcGauge pct={score} color={color} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {quizData.length === 0 && (
        <div
          style={{
            background: "#fff",
            borderRadius: 14,
            padding: "32px 20px",
            boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
            textAlign: "center",
            color: "#aaa",
            fontSize: 14,
          }}
        >
          No quiz records found.
        </div>
      )}
    </div>
  );
};

export default ScoreBoardTable;
